import React, { useEffect, useState, useMemo, useRef } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender
} from '@tanstack/react-table';
import SearchIcon from '@mui/icons-material/Search';
import SourceIcon from '@mui/icons-material/Source';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { toast } from 'react-toastify';
import { sendRequest } from '../../utils/dateHelpers';
import EmptyState from '../Common/EmptyState';
import BrandedSelect from '../Form/BrandedSelect';
import Loading from '../Loading';
import { formatIntegrationType } from '../../utils/integrations';

const getDefaultBranchValue = repo =>
  repo.default_branch || repo.defaultBranch || repo.branch || '';

const normalizeProjectNames = projectValue => {
  if (Array.isArray(projectValue)) {
    return projectValue.flatMap(normalizeProjectNames).filter(Boolean);
  }

  if (typeof projectValue === 'string') {
    const trimmedProjectName = projectValue.trim();
    return trimmedProjectName ? [trimmedProjectName] : [];
  }

  if (projectValue && typeof projectValue === 'object') {
    return normalizeProjectNames(
      projectValue.name || projectValue.project || projectValue.title || ''
    );
  }

  return [];
};

const getProjectSearchValue = repo =>
  normalizeProjectNames(repo.project).join(', ');

const getRepositoryIntegrationLabel = repo =>
  repo.providerLabel ||
  repo.integrationLabel ||
  formatIntegrationType(repo.leadType || repo.provider_type || repo.provider);

const getRepositoryNameSearchValue = repo =>
  [
    repo.display_name,
    repo.repo,
    repo.full_name,
    getRepositoryIntegrationLabel(repo)
  ]
    .filter(Boolean)
    .join(' ');

const getRepositoryStatusClasses = repo =>
  repo?.status === 'active'
    ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200'
    : 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';

const getRepositoryStatusLabel = repo =>
  repo?.statusLabel || (repo?.status === 'active' ? 'Active' : 'Passive');

const getRepositoryReasonLabel = repo =>
  repo?.status === 'active' ? '' : repo?.reasonLabel || '-';

const Repositories = () => {
  const [data, setData] = useState([]);
  const [editingState, setEditingState] = useState(null);
  const [globalFilter, setGlobalFilter] = useState('');
  const [projectFilter, setProjectFilter] = useState('all');
  const [selectedRepositoryIds, setSelectedRepositoryIds] = useState([]);
  const [bulkWeight, setBulkWeight] = useState(1);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [sorting, setSorting] = useState([]);
  const [isDarkMode, setIsDarkMode] = useState(
    () => document?.body?.classList.contains('dark') || false
  );
  const branchDraftRef = useRef('');
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });

  useEffect(() => {
    const syncDarkMode = () => {
      setIsDarkMode(document.body.classList.contains('dark'));
    };

    syncDarkMode();

    const observer = new MutationObserver(syncDarkMode);
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  const fetchRepos = async () => {
    try {
      const response = await sendRequest('/repos/get', 'GET');
      setData(response.data);
    } catch (error) {
      console.error('Error fetching repos:', error);
    }
  };

  useEffect(() => {
    fetchRepos();
  }, []);

  const repositoryStats = useMemo(() => {
    const totalRepositories = data.length;
    const projectCount = new Set(
      data.flatMap(repo => normalizeProjectNames(repo.project))
    ).size;

    return [
      {
        label: 'Repositories',
        value: totalRepositories,
        icon: SourceIcon,
        tone: 'border-blue-100 bg-gradient-to-br from-blue-50 to-white text-blue-700'
      },
      {
        label: 'Projects',
        value: projectCount,
        icon: AccountTreeOutlinedIcon,
        tone: 'border-emerald-100 bg-gradient-to-br from-emerald-50 to-white text-emerald-700'
      }
    ];
  }, [data]);

  const projectOptions = useMemo(() => {
    const projects = new Set();

    data.forEach(repo => {
      const projectNames = normalizeProjectNames(repo.project);
      if (projectNames.length) {
        projectNames.forEach(projectName => projects.add(projectName));
      } else {
        projects.add('Unassigned');
      }
    });

    return Array.from(projects).sort((first, second) =>
      first.localeCompare(second)
    );
  }, [data]);

  const projectFilteredData = useMemo(() => {
    if (projectFilter === 'all') return data;

    return data.filter(repo => {
      const projectNames = normalizeProjectNames(repo.project);
      return projectFilter === 'Unassigned'
        ? projectNames.length === 0
        : projectNames.includes(projectFilter);
    });
  }, [data, projectFilter]);

  const projectFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All projects' },
      ...projectOptions.map(projectName => ({
        value: projectName,
        label: projectName
      }))
    ],
    [projectOptions]
  );

  const selectedRepositories = useMemo(
    () => data.filter(repo => selectedRepositoryIds.includes(repo._id)),
    [data, selectedRepositoryIds]
  );

  const toggleRepositorySelection = repositoryId => {
    setSelectedRepositoryIds(currentIds =>
      currentIds.includes(repositoryId)
        ? currentIds.filter(id => id !== repositoryId)
        : [...currentIds, repositoryId]
    );
  };

  const toggleAllFilteredRepositories = () => {
    const filteredIds = table
      .getFilteredRowModel()
      .rows.map(row => row.original._id);
    const allSelected = filteredIds.every(id =>
      selectedRepositoryIds.includes(id)
    );

    setSelectedRepositoryIds(currentIds =>
      allSelected
        ? currentIds.filter(id => !filteredIds.includes(id))
        : Array.from(new Set([...currentIds, ...filteredIds]))
    );
  };

  const handleBulkWeightUpdate = async () => {
    const weight = Number(bulkWeight);
    if (
      !selectedRepositories.length ||
      !Number.isFinite(weight) ||
      weight < 0.1
    ) {
      toast.error('Select repositories and enter a valid weight.');
      return;
    }

    setIsBulkUpdating(true);
    try {
      const responses = await Promise.all(
        selectedRepositories.map(repo =>
          sendRequest('/repos/edit', 'POST', {
            uid: repo._id,
            weight
          })
        )
      );
      const failedUpdates = responses.filter(response => !response.success);

      if (failedUpdates.length) {
        throw new Error('Some repositories could not be updated.');
      }

      setSelectedRepositoryIds([]);
      await fetchRepos();
      toast.success(`${selectedRepositories.length} repositories updated.`);
    } catch (error) {
      console.error('Error updating repository weights:', error);
      toast.error('Repository weights could not be updated.');
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleEdit = (row, field) => {
    const nextValue =
      field === 'weight'
        ? row.original.weight
        : getDefaultBranchValue(row.original);

    if (field === 'defaultBranch') {
      branchDraftRef.current = nextValue;
    }

    setEditingState({
      rowId: row.original._id,
      field,
      value: nextValue
    });
  };

  const handleSave = async row => {
    if (!editingState) return;

    try {
      const payload = {
        uid: row.original._id
      };

      if (editingState.field === 'weight') {
        payload.weight = Number(editingState.value);
      }

      if (editingState.field === 'defaultBranch') {
        if (!branchDraftRef.current.trim()) {
          toast.error('Default branch is required.');
          return;
        }

        payload.default_branch = branchDraftRef.current.trim();
      }

      const response = await sendRequest('/repos/edit', 'POST', payload);
      if (response.success) {
        setEditingState(null);
        await fetchRepos();
        toast.success('Repository updated.');
      }
    } catch (error) {
      console.error('Error updating repository:', error);
      toast.error('Repository could not be updated.');
    }
  };

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        header: () => {
          const filteredRows = table.getFilteredRowModel().rows;
          const allFilteredSelected =
            filteredRows.length > 0 &&
            filteredRows.every(row =>
              selectedRepositoryIds.includes(row.original._id)
            );

          return (
            <input
              type="checkbox"
              aria-label="Select all filtered repositories"
              checked={allFilteredSelected}
              onChange={toggleAllFilteredRepositories}
              onClick={event => event.stopPropagation()}
              className="h-4 w-4 rounded border-slate-300 text-blue-600"
            />
          );
        },
        cell: ({ row }) => (
          <input
            type="checkbox"
            aria-label={`Select ${row.original.display_name || row.original.repo}`}
            checked={selectedRepositoryIds.includes(row.original._id)}
            onChange={() => toggleRepositorySelection(row.original._id)}
            onClick={event => event.stopPropagation()}
            className="h-4 w-4 rounded border-slate-300 text-blue-600"
          />
        )
      },
      {
        header: 'Repository Name',
        accessorFn: row => getRepositoryNameSearchValue(row),
        id: 'repo',
        cell: ({ row }) => (
          <div className="flex items-center gap-3">
            <div
              className={`flex h-10 w-10 items-center justify-center rounded-xl ${
                isDarkMode
                  ? 'bg-blue-500/15 text-blue-300'
                  : 'bg-blue-50 text-blue-600'
              }`}
            >
              <SourceIcon fontSize="small" />
            </div>
            <div className="min-w-0">
              <span
                title={row.original.full_name || row.original.repo}
                className={`block truncate font-semibold ${
                  isDarkMode ? 'text-white' : 'text-slate-800'
                }`}
              >
                {row.original.display_name || row.original.repo}
              </span>
              <span
                className={`block text-xs ${
                  isDarkMode ? 'text-slate-400' : 'text-slate-400'
                }`}
              >
                {getRepositoryIntegrationLabel(row.original)}
              </span>
            </div>
          </div>
        )
      },
      {
        header: 'Project Name',
        accessorFn: row => getProjectSearchValue(row),
        id: 'project',
        cell: ({ row }) => {
          const projectNames = normalizeProjectNames(row.original.project);

          if (!projectNames.length) {
            return (
              <div
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${
                  isDarkMode
                    ? 'border-strokedark bg-meta-4 text-slate-200'
                    : 'border-slate-200 bg-slate-50 text-slate-600'
                }`}
              >
                <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                Unassigned
              </div>
            );
          }

          return (
            <div className="flex flex-wrap items-center gap-2">
              {projectNames.map(projectName => (
                <div
                  key={`${row.original._id}-${projectName}`}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${
                    isDarkMode
                      ? 'border-strokedark bg-meta-4 text-slate-200'
                      : 'border-slate-200 bg-slate-50 text-slate-600'
                  }`}
                >
                  <AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />
                  {projectName}
                </div>
              ))}
            </div>
          );
        }
      },
      {
        header: 'Status',
        accessorFn: row => getRepositoryStatusLabel(row),
        id: 'status',
        cell: ({ row }) => (
          <span
            className={`inline-flex w-fit rounded-full border px-3 py-1 text-xs font-semibold ${getRepositoryStatusClasses(
              row.original
            )}`}
          >
            {getRepositoryStatusLabel(row.original)}
          </span>
        )
      },
      {
        header: 'Reason',
        accessorFn: row => getRepositoryReasonLabel(row),
        id: 'reason',
        cell: ({ row }) => {
          const reasonLabel = getRepositoryReasonLabel(row.original);

          return (
            <span
              className={`text-sm ${
                row.original.status === 'active'
                  ? isDarkMode
                    ? 'text-slate-500'
                    : 'text-slate-400'
                  : isDarkMode
                    ? 'text-slate-300'
                    : 'text-slate-600'
              }`}
            >
              {reasonLabel || '-'}
            </span>
          );
        }
      },
      {
        header: 'Weight',
        accessorKey: 'weight',
        cell: ({ row }) => {
          const isEditing =
            editingState?.rowId === row.original._id &&
            editingState?.field === 'weight';

          return (
            <div
              className="flex min-w-[120px] items-center gap-2"
              style={{ width: '200px' }}
            >
              {!isEditing && (
                <span
                  className={`inline-flex min-w-[56px] items-center justify-center rounded-full px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ${
                    isDarkMode
                      ? 'bg-blue-500/15 text-blue-300 ring-blue-500/20'
                      : 'bg-blue-50 text-blue-700 ring-blue-100'
                  }`}
                >
                  {row.original.weight}
                </span>
              )}

              {isEditing ? (
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    value={editingState?.value ?? ''}
                    onChange={e =>
                      setEditingState(prev => ({
                        ...prev,
                        value: Math.max(0.1, Number(e.target.value))
                      }))
                    }
                    className={`w-20 rounded-xl border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                      isDarkMode
                        ? 'border-strokedark bg-meta-4 text-slate-100 focus:ring-blue-900/40'
                        : 'border-slate-200 bg-white text-slate-700 focus:ring-blue-200'
                    }`}
                  />
                  <button
                    onClick={() => handleSave(row)}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingState(null)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                      isDarkMode
                        ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => handleEdit(row, 'weight')}
                  className={`rounded-xl px-3 py-2 text-xs font-semibold text-white transition ${
                    isDarkMode
                      ? 'bg-blue-600 hover:bg-blue-500'
                      : 'bg-slate-900 hover:bg-slate-800'
                  }`}
                >
                  Edit
                </button>
              )}
            </div>
          );
        }
      },
      {
        header: () => (
          <div className="flex items-center gap-2">
            <span>Analysis Branch</span>
            <span
              className="group relative flex items-center normal-case"
              onClick={event => event.stopPropagation()}
            >
              <button
                type="button"
                aria-label="Analysis branch information"
                className={`inline-flex items-center justify-center rounded-full transition ${
                  isDarkMode
                    ? 'text-slate-400 hover:text-slate-200'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              </button>
              <span
                className={`pointer-events-none absolute left-1/2 top-full z-50 mt-2 w-64 -translate-x-1/2 rounded-lg px-2 py-1.5 text-xs font-medium tracking-normal opacity-0 shadow-lg transition-opacity group-hover:opacity-100 group-focus-within:opacity-100 ${
                  isDarkMode
                    ? 'bg-slate-950 text-white'
                    : 'bg-slate-900 text-white'
                }`}
              >
                Analysis will be performed based on the commits on the branch
                selected here.
              </span>
            </span>
          </div>
        ),
        accessorFn: row => getDefaultBranchValue(row),
        id: 'defaultBranch',
        cell: ({ row }) => {
          const repoId = row.original._id;
          const isEditing =
            editingState?.rowId === repoId &&
            editingState?.field === 'defaultBranch';
          const defaultBranch = getDefaultBranchValue(row.original);

          return (
            <div className="flex min-w-[220px] items-center gap-2">
              {isEditing ? (
                <>
                  <input
                    type="text"
                    key={`${repoId}-${defaultBranch}`}
                    defaultValue={editingState?.value ?? ''}
                    onChange={e => {
                      branchDraftRef.current = e.target.value;
                    }}
                    autoFocus
                    placeholder="Enter analysis branch"
                    className={`min-w-[200px] rounded-xl border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                      isDarkMode
                        ? 'border-strokedark bg-meta-4 text-slate-100 focus:ring-blue-900/40'
                        : 'border-slate-200 bg-white text-slate-700 focus:ring-blue-200'
                    }`}
                  />
                  <button
                    onClick={() => handleSave(row)}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:bg-emerald-300"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingState(null)}
                    className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                      isDarkMode
                        ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4'
                        : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                <>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1.5 text-sm font-semibold ring-1 ring-inset ${
                      isDarkMode
                        ? 'bg-slate-800 text-slate-200 ring-strokedark'
                        : 'bg-slate-100 text-slate-700 ring-slate-200'
                    }`}
                  >
                    {defaultBranch || '-'}
                  </span>
                  <button
                    onClick={() => handleEdit(row, 'defaultBranch')}
                    className={`rounded-xl px-3 py-2 text-xs font-semibold text-white transition ${
                      isDarkMode
                        ? 'bg-blue-600 hover:bg-blue-500'
                        : 'bg-slate-900 hover:bg-slate-800'
                    }`}
                  >
                    Edit
                  </button>
                </>
              )}
            </div>
          );
        }
      }
    ],
    [editingState, isDarkMode, selectedRepositoryIds]
  );

  const table = useReactTable({
    data: projectFilteredData,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel()
  });

  const hasRepositories = data.length > 0;
  const hasVisibleRows = table.getRowModel().rows.length > 0;
  const emptyMessage = globalFilter
    ? 'No repositories found for your search.'
    : 'No repository data found yet.';

  return (
    <div
      className={`overflow-hidden rounded-sm border shadow-default ${
        isDarkMode ? 'border-strokedark bg-boxdark' : 'border-stroke bg-white'
      }`}
    >
      <div
        className={`border-b px-6 py-6 ${
          isDarkMode
            ? 'border-strokedark bg-boxdark'
            : 'border-stroke bg-gradient-to-r from-slate-50 via-white to-blue-50/70'
        }`}
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4 xl:items-stretch">
          {repositoryStats.map(stat => {
            const Icon = stat.icon;

            return (
              <div
                key={stat.label}
                className={`rounded-2xl border p-4 shadow-sm ${
                  isDarkMode
                    ? 'border-strokedark bg-meta-4 text-slate-200'
                    : stat.tone
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p
                      className={`text-xs font-semibold uppercase tracking-[0.16em] ${
                        isDarkMode ? 'text-slate-400' : 'text-slate-500'
                      }`}
                    >
                      {stat.label}
                    </p>
                    <p
                      className={`mt-2 text-2xl font-bold ${
                        isDarkMode ? 'text-white' : 'text-slate-900'
                      }`}
                    >
                      {stat.value}
                    </p>
                  </div>
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-xl shadow-sm ${
                      isDarkMode ? 'bg-boxdark' : 'bg-white/80'
                    }`}
                  >
                    <Icon fontSize="small" />
                  </div>
                </div>
              </div>
            );
          })}

          <div
            className={`rounded-2xl border p-4 shadow-sm ${
              isDarkMode
                ? 'border-strokedark bg-meta-4'
                : 'border-slate-200 bg-white'
            }`}
          >
            <BrandedSelect
              label="Project filter"
              value={projectFilter}
              onChange={nextProject => {
                setProjectFilter(nextProject || 'all');
                setPagination(current => ({ ...current, pageIndex: 0 }));
              }}
              options={projectFilterOptions}
              leadingIcon={AccountTreeOutlinedIcon}
              placeholder="Search projects..."
              isSearchable
              isDarkMode={isDarkMode}
              minHeight={52}
              maxMenuHeight={280}
              noOptionsMessage={() => 'No projects found.'}
            />
          </div>

          <div
            className={`rounded-2xl border p-4 shadow-sm ${
              isDarkMode
                ? 'border-strokedark bg-meta-4'
                : 'border-slate-200 bg-white'
            }`}
          >
            <label
              htmlFor="repository-search"
              className={`mb-2 block text-sm font-medium ${
                isDarkMode ? 'text-slate-200' : 'text-slate-700'
              }`}
            >
              Repository search
            </label>
            <div className="relative">
              <SearchIcon
                className={`absolute left-4 top-1/2 -translate-y-1/2 ${
                  isDarkMode ? 'text-slate-500' : 'text-slate-400'
                }`}
              />
              <input
                id="repository-search"
                type="text"
                value={globalFilter || ''}
                onChange={e => setGlobalFilter(e.target.value)}
                placeholder="Search repositories or projects..."
                className={`h-13 w-full rounded-2xl border pl-12 pr-4 text-sm shadow-sm outline-none transition-all placeholder:text-slate-400 focus:ring-4 ${
                  isDarkMode
                    ? 'border-strokedark bg-boxdark text-white placeholder:text-slate-500 focus:border-blue-400 focus:ring-blue-900/40'
                    : 'border-slate-200 bg-white text-slate-700 focus:border-blue-300 focus:ring-blue-100'
                }`}
              />
            </div>
          </div>
        </div>
      </div>

      {selectedRepositories.length > 0 && (
        <div
          className={`flex flex-col gap-3 border-b px-6 py-4 md:flex-row md:items-center md:justify-between ${
            isDarkMode
              ? 'border-strokedark bg-blue-500/10'
              : 'border-blue-100 bg-blue-50/70'
          }`}
        >
          <div>
            <p
              className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}
            >
              {selectedRepositories.length} repositories selected
            </p>
            <p
              className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
            >
              Apply one weight to all selected repositories.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="number"
              min={0.1}
              step={0.1}
              value={bulkWeight}
              onChange={event => setBulkWeight(event.target.value)}
              aria-label="Bulk repository weight"
              className={`w-24 rounded-xl border px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 ${
                isDarkMode
                  ? 'border-strokedark bg-meta-4 text-slate-100 focus:ring-blue-900/40'
                  : 'border-slate-200 bg-white text-slate-700 focus:ring-blue-100'
              }`}
            />
            <button
              onClick={handleBulkWeightUpdate}
              disabled={isBulkUpdating}
              className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isBulkUpdating ? <Loading type="inline" /> : 'Update weight'}
            </button>
            <button
              onClick={() => setSelectedRepositoryIds([])}
              disabled={isBulkUpdating}
              className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                isDarkMode
                  ? 'border-strokedark bg-boxdark text-slate-200 hover:bg-meta-4'
                  : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}
            >
              Clear
            </button>
          </div>
        </div>
      )}

      <div className="px-6 py-6">
        <div
          className={`overflow-hidden rounded-2xl border ${
            isDarkMode
              ? 'border-strokedark bg-boxdark'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse">
              <thead className={isDarkMode ? 'bg-meta-4' : 'bg-slate-50/90'}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        scope="col"
                        className={`cursor-pointer select-none border-b px-6 py-4 text-left text-xs font-semibold uppercase tracking-[0.16em] ${
                          isDarkMode
                            ? 'border-strokedark text-slate-400'
                            : 'border-slate-200 text-slate-500'
                        }`}
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: ' 🔼',
                          desc: ' 🔽'
                        }[header.column.getIsSorted()] ?? null}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {hasVisibleRows ? (
                  table.getRowModel().rows.map(row => (
                    <tr
                      key={row.id}
                      className={`border-b align-middle transition-colors duration-150 ${
                        isDarkMode
                          ? 'border-strokedark hover:bg-white/5'
                          : 'border-slate-100 hover:bg-slate-50/70'
                      }`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          className={`px-6 py-4 text-sm ${
                            isDarkMode ? 'text-slate-200' : 'text-slate-700'
                          }`}
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-10">
                      <EmptyState
                        compact
                        className={`rounded-2xl border-dashed px-4 py-10 ${
                          isDarkMode ? 'bg-meta-4' : 'bg-slate-50'
                        }`}
                        title="No Data Available"
                        description={emptyMessage}
                      />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {hasRepositories && (
        <div
          className={`flex flex-col gap-4 border-t px-6 pb-6 pt-5 md:flex-row md:items-center md:justify-between ${
            isDarkMode ? 'border-strokedark' : 'border-slate-100'
          }`}
        >
          <div>
            <button
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
              className={`mr-1 rounded-lg border px-3 py-2 text-sm transition disabled:opacity-50 ${
                isDarkMode
                  ? 'border-strokedark text-slate-200 hover:bg-meta-4'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {'<<'}
            </button>
            <button
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className={`mr-1 rounded-lg border px-3 py-2 text-sm transition disabled:opacity-50 ${
                isDarkMode
                  ? 'border-strokedark text-slate-200 hover:bg-meta-4'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {'<'}
            </button>
            <button
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className={`mr-1 rounded-lg border px-3 py-2 text-sm transition disabled:opacity-50 ${
                isDarkMode
                  ? 'border-strokedark text-slate-200 hover:bg-meta-4'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {'>'}
            </button>
            <button
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
              className={`rounded-lg border px-3 py-2 text-sm transition disabled:opacity-50 ${
                isDarkMode
                  ? 'border-strokedark text-slate-200 hover:bg-meta-4'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {'>>'}
            </button>
          </div>

          <span
            className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}
          >
            Page{' '}
            <strong
              className={`font-semibold ${
                isDarkMode ? 'text-white' : 'text-slate-800'
              }`}
            >
              {table.getState().pagination.pageIndex + 1} of{' '}
              {table.getPageCount()}
            </strong>
          </span>

          <select
            value={table.getState().pagination.pageSize}
            onChange={e => table.setPageSize(Number(e.target.value))}
            className={`rounded-xl border px-3 py-2 text-sm outline-none transition focus:ring-2 ${
              isDarkMode
                ? 'border-strokedark bg-meta-4 text-slate-200 focus:ring-blue-900/40'
                : 'border-slate-200 bg-white text-slate-700 focus:ring-blue-100'
            }`}
          >
            {[10, 20, 30, 50].map(pageSize => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </select>
        </div>
      )}

      <div
        className={`border-t px-6 pb-6 ${isDarkMode ? 'border-strokedark' : 'border-slate-100'}`}
      >
        <div
          className={`mt-5 flex w-full rounded-2xl border px-4 py-4 shadow-sm ${
            isDarkMode
              ? 'border-amber-500/30 bg-amber-500/10'
              : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50'
          }`}
        >
          <div className="w-full">
            <p
              className={`text-sm leading-relaxed ${
                isDarkMode ? 'text-amber-200' : 'text-amber-800'
              }`}
            >
              Repository weights change how strongly a repo contributes to
              effort calculations, while the analysis branch keeps analysis
              focused on the branch your team actually ships from.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Repositories;
